import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { mfaApi } from "@/lib/api/mfa";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { Spinner } from "@/components/ui/Spinner";

/**
 * Forced TOTP enrollment - shown mid-login when an account's MFA is
 * required but not yet enrolled (LoginResult.mfa_setup_required). Reuses
 * the exact same /auth/mfa/enroll and /auth/mfa/confirm endpoints a
 * voluntary self-service setup would call (see mfaApi) - the only
 * difference here is which token authorises the call: setupToken, a
 * short-lived, permission-free TokenMFASetup the backend issued in place
 * of a real session.
 *
 * No QR code image is rendered: generating one client-side without a
 * bundled library would mean either shipping a new dependency or sending
 * the TOTP secret to a third-party QR-rendering service, and the secret is
 * exactly the thing that must never leave this device unencrypted. Manual
 * key entry (shown here) is a standard, always-available fallback in every
 * authenticator app - not a degraded experience.
 */
export function MFASetupPanel({ setupToken, onComplete }: { setupToken: string; onComplete: () => void }) {
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const signOut = useAuthStore((s) => s.signOut);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // The setup token is authorised the same way a normal access token is -
  // via the Authorization header the api client's interceptor already
  // attaches from the auth store - so mounting this panel simply installs
  // it there for the duration of enrollment. It carries no permissions and
  // expires in minutes; signOut() below clears it again once enrollment
  // finishes (successfully or not), so it never lingers as a stray token.
  useEffect(() => {
    setAccessToken(setupToken);
    return () => signOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupToken]);

  const enrollment = useQuery({
    queryKey: ["mfa", "setup-enrollment", setupToken],
    queryFn: () => mfaApi.beginSetupEnrollment(),
  });

  const confirm = useMutation({
    mutationFn: () => mfaApi.confirmSetupEnrollment(code),
    onSuccess: (result) => setBackupCodes(result.backup_codes),
  });

  if (backupCodes) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Save your backup codes</h2>
          <p className="mt-1 text-sm text-slate-500">
            Each code can be used once in place of your authenticator app if you lose access to it. Store them
            somewhere safe - they will not be shown again.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 px-4 py-3 font-mono text-sm">
          {backupCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <Button onClick={onComplete} fullWidth>
          I've saved these codes - continue
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Set up multi-factor authentication</h2>
        <p className="mt-1 text-sm text-slate-500">
          This account requires MFA. Add it to your authenticator app (Google Authenticator, Authy, 1Password, ...)
          using the key below, then enter the 6-digit code it generates.
        </p>
      </div>

      {enrollment.isLoading && <Spinner />}
      {enrollment.isError && <ErrorMessage error={enrollment.error} />}
      {enrollment.data && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Manual entry key</p>
          <code className="mt-1 block break-all font-mono text-sm text-slate-800">{enrollment.data.secret}</code>
        </div>
      )}

      <Input
        label="Authentication code"
        placeholder="123456"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      {confirm.isError && <ErrorMessage error={confirm.error} />}

      <Button onClick={() => confirm.mutate()} loading={confirm.isPending} disabled={!code} fullWidth>
        Verify and enable MFA
      </Button>
    </div>
  );
}
